import { useRouter } from 'next/router';
import { useMemo } from 'react';

import useCanonicalization from 'hooks/useCanonicalization';
import getCategoryMediaUrlPrefix from 'utils/getCategoryMediaUrlPrefix';
import { IMAGE_ROUTE_PREFIXES } from 'tmp/main';

import { generateDescriptionList } from '../common/HashTagLinks/utils';

const ALLOWED_TITLE_LENGTH = 60;
const ALLOWED_DESCRIPTION_LENGTH = 160;

type MetaDataProps = {
  username: string;
  tags?: string;
  description?: string;
  type?: string;
};

function reduceString(splitArr: string[], initialString: string, size: number) {
  const len = splitArr.length;
  let result = initialString;
  for (let i = 0; i < len; i++) {
    const item = splitArr[i];
    const currentStrLength = item.length;
    const addedLength = currentStrLength + result.length;
    if (addedLength < size) {
      result = `${result} ${item}`;
      continue;
    }
    break;
  }
  return result;
}

function generateMetaDescription({ username, tags, description, type = 'image' }: MetaDataProps) {
  const GENERATED_META = `Explore awesome ${type}s by @${username}. This visual is about`;

  return reduceString(
    [...tags.split(' '), ...description.split(' ')],
    GENERATED_META,
    ALLOWED_DESCRIPTION_LENGTH,
  );
}

function generateMetaTitle({ username, tags, description, type }: MetaDataProps) {
  const GENERATED_META = ` ${type} by @${username}`;

  const INITIAL_LENGTH = type.length + username.length + 4;

  return (
    reduceString(
      [...tags.split(' '), ...description.split(' ')],
      '',
      ALLOWED_TITLE_LENGTH - INITIAL_LENGTH,
    ) + GENERATED_META
  );
}

export default function useSingleViewMeta({ data, domain }) {
  const { asPath, query } = useRouter();
  const id = query.id as string;
  const [type] = id.split('-');

  let imagePath = domain + asPath;

  if (!IMAGE_ROUTE_PREFIXES.includes(type)) {
    const { url, type: imageType } = data;
    const imageRoutePrefix = getCategoryMediaUrlPrefix(imageType, url);
    imagePath = `${domain}/i/${imageRoutePrefix}-${id}`;
  }
  const [canonicalUrl] = useCanonicalization(imagePath, false);

  const metaData = useMemo(() => {
    const [description] = generateDescriptionList(data.title);
    const tags = `${data.tags.reduce((acc, current) => `${acc} ${current}`, '')}`;

    if (description && !!tags.length) {
      const metaTitle = generateMetaTitle({
        username: data.user.username,
        tags,
        description,
        type,
      });

      const metaDescription = generateMetaDescription({
        username: data.user.username,
        tags,
        description,
        type,
      });

      return {
        metaTitle,
        metaDescription,
      };
    }

    if (description && !data.tags.length) {
      const metaTitle = generateMetaTitle({
        username: data.user.username,
        tags: '',
        description,
        type,
      });

      const metaDescription = generateMetaDescription({
        username: data.user.username,
        tags: '',
        description,
        type,
      });

      return {
        metaTitle,
        metaDescription,
      };
    }

    if (!description && !!data.tags.length) {
      const metaTitle = generateMetaTitle({
        username: data.user.username,
        tags,
        description: '',
        type,
      });

      const metaDescription = generateMetaDescription({
        username: data.user.username,
        tags,
        description: '',
        type,
      });

      return { metaTitle, metaDescription };
    }

    return { metaTitle: null, metaDescription: null };
  }, [data.tags, data.title, query.id, data.user.username]);

  return {
    metaTitle: metaData?.metaTitle?.trim(),
    metaDescription: metaData?.metaDescription?.trim(),
    canonicalUrl,
  };
}
